package top.wcpe.allincore.ui.runtime.style

import top.wcpe.allincore.ui.runtime.render.model.GradientStop
import top.wcpe.allincore.ui.runtime.render.model.GradientType
import top.wcpe.allincore.ui.runtime.render.model.GuiBlendMode
import top.wcpe.allincore.ui.runtime.render.model.MaskType

/**
 * 视觉样式子段：圆角 / 边框 / 渐变 / 阴影 / 遮罩 / Neon / Ripple / 程序纹理 / Flow / Energy。
 *
 * 历史上各字段由 Lombok `@Getter @Setter`（配合 `lombok.config` 的 `accessors.fluent`
 * 与 `accessors.chain`）生成 fluent 风格、链式返回 `this` 的访问器。改写为 Kotlin 后，
 * 仍以重载方法 `x()` / `x(value)` 复刻该 ABI：`x()` 取值、`x(value)` 写值并返回 `this`
 * 以保持链式调用。各字段以私有 `xField` 作 backing。
 *
 * 以下方法语义不是单字段直写，保持手写：
 * - [cornerRadius]：统一设置四角
 * - [cornerRadii]：[CornerRadii] 聚合视图
 * - [gradientStops] / [boxShadows] 的写入：null 转空 List 并做防御性复制
 * - [hasStyledEffect] / [hasShadow] 等派生判定
 * - [copyFrom]：字段级拷贝
 */
@Suppress("MagicNumber", "TooManyFunctions")
class StyleVisual {

    private var cornerRadiusTLField: Float = 0f
    private var cornerRadiusTRField: Float = 0f
    private var cornerRadiusBRField: Float = 0f
    private var cornerRadiusBLField: Float = 0f
    private var borderWidthField: Float = 0f
    private var borderColorField: Int = 0

    private var gradientTypeField: GradientType = GradientType.NONE
    private var gradientAngleRadiansField: Float = 0f

    /** 渐变停靠点；写入由手写 [gradientStops] 接管（null 转空 List + 防御性复制）。 */
    private var gradientStopsField: List<GradientStop> = emptyList()

    /** 径向渐变中心，0..1 比例，默认 (0.5, 0.5)。 */
    private var radialCenterXField: Float = 0.5f
    private var radialCenterYField: Float = 0.5f

    /** 径向渐变最大半径，相对短边的比例；默认 1.0。 */
    private var radialRadiusField: Float = 1f

    // ========== 阴影 ==========
    private var shadowOffsetXField: Float = 0f
    private var shadowOffsetYField: Float = 0f
    private var shadowBlurField: Float = 0f
    private var shadowColorField: Int = 0
    private var shadowSpreadField: Float = 0f

    /** 单阴影是否为内阴影（inset）；默认 false（外阴影）。 */
    private var shadowInsetField: Boolean = false

    /** 多重阴影列表（CSS box-shadow 语义）；写入由手写 [boxShadows] 接管。 */
    private var boxShadowsField: List<ShadowSpec> = emptyList()

    // ========== 遮罩 ==========
    private var maskTypeField: MaskType = MaskType.NONE
    private var maskParam1Field: Float = 0f
    private var maskParam2Field: Float = 0f
    private var maskParam3Field: Float = 0f

    // ========== M9.2 霓虹发光 ==========
    private var neonColorField: Int = 0
    private var neonIntensityField: Float = 0f
    private var neonSizeField: Float = 0f
    private var neonAnimatedField: Boolean = false

    // ========== M9.2 涟漪 ==========
    private var rippleColorField: Int = 0
    private var rippleDurationField: Float = 0f
    private var rippleRadiusField: Float = 0f

    // ========== 程序纹理（第一批 8 种） ==========
    private var proceduralTextureKindField: String? = null
    private var proceduralColorAField: Int = 0
    private var proceduralColorBField: Int = 0
    private var proceduralScaleField: Float = 8f
    private var proceduralDensityField: Float = 1f
    private var proceduralIntensityField: Float = 1f

    // ========== 流光（linear / radial / diagonal） ==========
    private var flowColorField: Int = 0
    private var flowSpeedField: Float = 0f
    private var flowWidthField: Float = 0f
    private var flowAngleDegField: Float = 0f

    /** 0 线性 / 1 环形 / 2 对角线。 */
    private var flowModeField: Int = 0

    /**
     * 仅 ProgressBar 生效：true 时流光仅在前景（进度部分）流动，而不是横跨整个控件矩形。
     * BoxRenderer / ButtonRenderer 忽略此字段。
     */
    private var flowOnProgressField: Boolean = false

    // ========== 能量场（pulse / wave / arc / plasma） ==========
    private var energyColorField: Int = 0
    private var energyIntensityField: Float = 0f
    private var energyFrequencyField: Float = 4f
    private var energySpeedField: Float = 1f

    /** 0 脉冲 / 1 波浪 / 2 电弧 / 3 等离子。 */
    private var energyPatternField: Int = 0

    // ========== E1 色彩矩阵 filter（CSS filter 子集） ==========
    // 各字段默认值即「identity（无变化）」，保证零回归；合成与逐像素应用见 ColorMatrix。
    private var filterBrightnessField: Float = 1f
    private var filterContrastField: Float = 1f
    private var filterGrayscaleField: Float = 0f
    private var filterSepiaField: Float = 0f
    private var filterHueRotateDegField: Float = 0f
    private var filterSaturateField: Float = 1f
    private var filterInvertField: Float = 0f

    // ========== E2 高斯模糊 filter（CSS filter: blur） ==========
    /** 高斯模糊半径（像素），0 表示不模糊（默认，零回归——完全不走 FBO 路径）。 */
    private var filterBlurField: Float = 0f

    // ========== #8 backdrop-filter 背景模糊（毛玻璃） ==========
    /** 背景模糊半径（像素），0 表示无 backdrop（默认，零回归）。 */
    private var backdropBlurField: Float = 0f

    // ========== #8 mix-blend-mode 混合模式 ==========
    /** GUI 混合模式，默认 [GuiBlendMode.NORMAL]（不改变合成，零回归）。 */
    private var blendModeField: GuiBlendMode = GuiBlendMode.NORMAL

    // ========== 圆角 ==========

    fun cornerRadiusTL(): Float = cornerRadiusTLField
    fun cornerRadiusTL(value: Float): StyleVisual = apply { cornerRadiusTLField = value }

    fun cornerRadiusTR(): Float = cornerRadiusTRField
    fun cornerRadiusTR(value: Float): StyleVisual = apply { cornerRadiusTRField = value }

    fun cornerRadiusBR(): Float = cornerRadiusBRField
    fun cornerRadiusBR(value: Float): StyleVisual = apply { cornerRadiusBRField = value }

    fun cornerRadiusBL(): Float = cornerRadiusBLField
    fun cornerRadiusBL(value: Float): StyleVisual = apply { cornerRadiusBLField = value }

    /** 统一设置四角圆角半径。 */
    fun cornerRadius(r: Float): StyleVisual = apply {
        cornerRadiusTLField = r
        cornerRadiusTRField = r
        cornerRadiusBRField = r
        cornerRadiusBLField = r
    }

    /** 把四角字段聚合为 [CornerRadii] 值对象，便于跨控件统一传递。 */
    fun cornerRadii(): CornerRadii =
        CornerRadii.of(cornerRadiusTLField, cornerRadiusTRField, cornerRadiusBRField, cornerRadiusBLField)

    /** 一次性设置四角；`radii == null` 视作 [CornerRadii.ZERO]。 */
    fun cornerRadii(radii: CornerRadii?): StyleVisual = apply {
        val r = radii ?: CornerRadii.ZERO
        cornerRadiusTLField = r.tl
        cornerRadiusTRField = r.tr
        cornerRadiusBRField = r.br
        cornerRadiusBLField = r.bl
    }

    // ========== 边框 ==========

    fun borderWidth(): Float = borderWidthField
    fun borderWidth(value: Float): StyleVisual = apply { borderWidthField = value }

    fun borderColor(): Int = borderColorField
    fun borderColor(value: Int): StyleVisual = apply { borderColorField = value }

    // ========== 渐变 ==========

    fun gradientType(): GradientType = gradientTypeField
    fun gradientType(value: GradientType): StyleVisual = apply { gradientTypeField = value }

    fun gradientAngleRadians(): Float = gradientAngleRadiansField
    fun gradientAngleRadians(value: Float): StyleVisual = apply { gradientAngleRadiansField = value }

    fun gradientStops(): List<GradientStop> = gradientStopsField

    /** 渐变停靠点：`null` 转空 List，并做防御性复制以隔离外部可变性。 */
    fun gradientStops(stops: List<GradientStop>?): StyleVisual = apply {
        gradientStopsField = if (stops == null) emptyList() else ArrayList(stops)
    }

    fun radialCenterX(): Float = radialCenterXField
    fun radialCenterX(value: Float): StyleVisual = apply { radialCenterXField = value }

    fun radialCenterY(): Float = radialCenterYField
    fun radialCenterY(value: Float): StyleVisual = apply { radialCenterYField = value }

    fun radialRadius(): Float = radialRadiusField
    fun radialRadius(value: Float): StyleVisual = apply { radialRadiusField = value }

    // ========== 阴影 ==========

    fun shadowOffsetX(): Float = shadowOffsetXField
    fun shadowOffsetX(value: Float): StyleVisual = apply { shadowOffsetXField = value }

    fun shadowOffsetY(): Float = shadowOffsetYField
    fun shadowOffsetY(value: Float): StyleVisual = apply { shadowOffsetYField = value }

    fun shadowBlur(): Float = shadowBlurField
    fun shadowBlur(value: Float): StyleVisual = apply { shadowBlurField = value }

    fun shadowColor(): Int = shadowColorField
    fun shadowColor(value: Int): StyleVisual = apply { shadowColorField = value }

    fun shadowSpread(): Float = shadowSpreadField
    fun shadowSpread(value: Float): StyleVisual = apply { shadowSpreadField = value }

    fun shadowInset(): Boolean = shadowInsetField
    fun shadowInset(value: Boolean): StyleVisual = apply { shadowInsetField = value }

    // ========== 多重阴影 ==========

    fun boxShadows(): List<ShadowSpec> = boxShadowsField

    /** 多重阴影：`null` 转空 List，并做防御性复制以隔离外部可变性。 */
    fun boxShadows(shadows: List<ShadowSpec>?): StyleVisual = apply {
        boxShadowsField = if (shadows == null) emptyList() else ArrayList(shadows)
    }

    /** 是否配置了任何可见的多重阴影。 */
    fun hasBoxShadows(): Boolean {
        if (boxShadowsField.isEmpty()) {
            return false
        }
        return boxShadowsField.any { it.isVisible() }
    }

    // ========== 遮罩 ==========

    fun maskType(): MaskType = maskTypeField
    fun maskType(value: MaskType): StyleVisual = apply { maskTypeField = value }

    fun maskParam1(): Float = maskParam1Field
    fun maskParam1(value: Float): StyleVisual = apply { maskParam1Field = value }

    fun maskParam2(): Float = maskParam2Field
    fun maskParam2(value: Float): StyleVisual = apply { maskParam2Field = value }

    fun maskParam3(): Float = maskParam3Field
    fun maskParam3(value: Float): StyleVisual = apply { maskParam3Field = value }

    // ========== 霓虹发光 ==========

    fun neonColor(): Int = neonColorField
    fun neonColor(value: Int): StyleVisual = apply { neonColorField = value }

    fun neonIntensity(): Float = neonIntensityField
    fun neonIntensity(value: Float): StyleVisual = apply { neonIntensityField = value }

    fun neonSize(): Float = neonSizeField
    fun neonSize(value: Float): StyleVisual = apply { neonSizeField = value }

    fun neonAnimated(): Boolean = neonAnimatedField
    fun neonAnimated(value: Boolean): StyleVisual = apply { neonAnimatedField = value }

    // ========== 涟漪 ==========

    fun rippleColor(): Int = rippleColorField
    fun rippleColor(value: Int): StyleVisual = apply { rippleColorField = value }

    fun rippleDuration(): Float = rippleDurationField
    fun rippleDuration(value: Float): StyleVisual = apply { rippleDurationField = value }

    fun rippleRadius(): Float = rippleRadiusField
    fun rippleRadius(value: Float): StyleVisual = apply { rippleRadiusField = value }

    // ========== 程序纹理 ==========

    fun proceduralTextureKind(): String? = proceduralTextureKindField
    fun proceduralTextureKind(value: String?): StyleVisual = apply { proceduralTextureKindField = value }

    fun proceduralColorA(): Int = proceduralColorAField
    fun proceduralColorA(value: Int): StyleVisual = apply { proceduralColorAField = value }

    fun proceduralColorB(): Int = proceduralColorBField
    fun proceduralColorB(value: Int): StyleVisual = apply { proceduralColorBField = value }

    fun proceduralScale(): Float = proceduralScaleField
    fun proceduralScale(value: Float): StyleVisual = apply { proceduralScaleField = value }

    fun proceduralDensity(): Float = proceduralDensityField
    fun proceduralDensity(value: Float): StyleVisual = apply { proceduralDensityField = value }

    fun proceduralIntensity(): Float = proceduralIntensityField
    fun proceduralIntensity(value: Float): StyleVisual = apply { proceduralIntensityField = value }

    // ========== 流光 ==========

    fun flowColor(): Int = flowColorField
    fun flowColor(value: Int): StyleVisual = apply { flowColorField = value }

    fun flowSpeed(): Float = flowSpeedField
    fun flowSpeed(value: Float): StyleVisual = apply { flowSpeedField = value }

    fun flowWidth(): Float = flowWidthField
    fun flowWidth(value: Float): StyleVisual = apply { flowWidthField = value }

    fun flowAngleDeg(): Float = flowAngleDegField
    fun flowAngleDeg(value: Float): StyleVisual = apply { flowAngleDegField = value }

    fun flowMode(): Int = flowModeField
    fun flowMode(value: Int): StyleVisual = apply { flowModeField = value }

    fun flowOnProgress(): Boolean = flowOnProgressField
    fun flowOnProgress(value: Boolean): StyleVisual = apply { flowOnProgressField = value }

    // ========== 能量场 ==========

    fun energyColor(): Int = energyColorField
    fun energyColor(value: Int): StyleVisual = apply { energyColorField = value }

    fun energyIntensity(): Float = energyIntensityField
    fun energyIntensity(value: Float): StyleVisual = apply { energyIntensityField = value }

    fun energyFrequency(): Float = energyFrequencyField
    fun energyFrequency(value: Float): StyleVisual = apply { energyFrequencyField = value }

    fun energySpeed(): Float = energySpeedField
    fun energySpeed(value: Float): StyleVisual = apply { energySpeedField = value }

    fun energyPattern(): Int = energyPatternField
    fun energyPattern(value: Int): StyleVisual = apply { energyPatternField = value }

    // ========== 色彩矩阵 filter ==========

    fun filterBrightness(): Float = filterBrightnessField
    fun filterBrightness(value: Float): StyleVisual = apply { filterBrightnessField = value }

    fun filterContrast(): Float = filterContrastField
    fun filterContrast(value: Float): StyleVisual = apply { filterContrastField = value }

    fun filterGrayscale(): Float = filterGrayscaleField
    fun filterGrayscale(value: Float): StyleVisual = apply { filterGrayscaleField = value }

    fun filterSepia(): Float = filterSepiaField
    fun filterSepia(value: Float): StyleVisual = apply { filterSepiaField = value }

    fun filterHueRotateDeg(): Float = filterHueRotateDegField
    fun filterHueRotateDeg(value: Float): StyleVisual = apply { filterHueRotateDegField = value }

    fun filterSaturate(): Float = filterSaturateField
    fun filterSaturate(value: Float): StyleVisual = apply { filterSaturateField = value }

    fun filterInvert(): Float = filterInvertField
    fun filterInvert(value: Float): StyleVisual = apply { filterInvertField = value }

    fun filterBlur(): Float = filterBlurField
    fun filterBlur(value: Float): StyleVisual = apply { filterBlurField = value }

    fun backdropBlur(): Float = backdropBlurField
    fun backdropBlur(value: Float): StyleVisual = apply { backdropBlurField = value }

    fun blendMode(): GuiBlendMode = blendModeField
    fun blendMode(value: GuiBlendMode): StyleVisual = apply { blendModeField = value }

    // ========== 快捷判断 ==========

    /** 是否有任何需要 styled 路径的视觉效果。 */
    fun hasStyledEffect(): Boolean = cornerRadiusTLField > 0 ||
        cornerRadiusTRField > 0 ||
        cornerRadiusBRField > 0 ||
        cornerRadiusBLField > 0 ||
        borderWidthField > 0 ||
        gradientTypeField != GradientType.NONE ||
        hasShadow() ||
        hasBoxShadows() ||
        maskTypeField != MaskType.NONE

    /**
     * 是否需要绘制阴影。
     *
     * 语义：必须同时满足「有几何参数」和「颜色非透明」——单独的 shadowColor 不能触发阴影，
     * 否则用户把 blur/offset/spread 全部归零仍会画一个填满整个矩形的实色阴影。
     */
    fun hasShadow(): Boolean {
        val hasGeometry = shadowBlurField > 0 ||
            shadowSpreadField > 0 ||
            shadowOffsetXField != 0f ||
            shadowOffsetYField != 0f
        val hasColor = (shadowColorField ushr 24) != 0
        return hasGeometry && hasColor
    }

    /**
     * 是否配置了任何非默认的色彩矩阵 filter。
     *
     * 任一字段偏离 identity 默认值（brightness/contrast/saturate=1，其余=0）即为 true，
     * 触发渲染期合成颜色矩阵并包住整棵子树。
     */
    fun hasColorFilter(): Boolean = filterBrightnessField != 1f ||
        filterContrastField != 1f ||
        filterGrayscaleField != 0f ||
        filterSepiaField != 0f ||
        filterHueRotateDegField != 0f ||
        filterSaturateField != 1f ||
        filterInvertField != 0f

    /**
     * 是否配置了需要离屏 FBO 路径的高斯模糊。`filterBlur > 0` 即为 true。
     * 与 [hasColorFilter] 正交：blur 走 FBO 捕获，色彩矩阵走逐图元。默认 0 时零回归。
     */
    fun hasBlur(): Boolean = filterBlurField > 0f

    /**
     * 是否配置了 backdrop-filter 背景模糊。`backdropBlur > 0` 即为 true。
     * 与 [hasBlur] 正交：backdrop 糊背景、filterBlur 糊元素子树整体。默认 0 时零回归。
     */
    fun hasBackdropBlur(): Boolean = backdropBlurField > 0f

    /**
     * 是否配置了非默认的 mix-blend-mode 混合模式。`blendMode != NORMAL` 即为 true。
     * 默认 [GuiBlendMode.NORMAL] 时返回 false，走默认正常混合（零回归）。
     */
    fun hasBlendMode(): Boolean = blendModeField != GuiBlendMode.NORMAL

    /** modifier 脱糖缓存使用的无分配内容指纹；只覆盖会进入 [StyleVisualModifiers] 的字段。 */
    internal fun modifierFingerprint(): Int {
        var result = 1
        fun mix(value: Int) {
            result = 31 * result + value
        }
        mix(cornerRadiusTLField.toBits())
        mix(cornerRadiusTRField.toBits())
        mix(cornerRadiusBRField.toBits())
        mix(cornerRadiusBLField.toBits())
        mix(borderWidthField.toBits())
        mix(borderColorField)
        mix(gradientTypeField.ordinal)
        mix(gradientAngleRadiansField.toBits())
        mix(gradientStopsField.hashCode())
        mix(radialCenterXField.toBits())
        mix(radialCenterYField.toBits())
        mix(radialRadiusField.toBits())
        mix(shadowOffsetXField.toBits())
        mix(shadowOffsetYField.toBits())
        mix(shadowBlurField.toBits())
        mix(shadowColorField)
        mix(shadowSpreadField.toBits())
        mix(shadowInsetField.hashCode())
        mix(boxShadowsField.hashCode())
        mix(maskTypeField.ordinal)
        mix(maskParam1Field.toBits())
        mix(maskParam2Field.toBits())
        mix(maskParam3Field.toBits())
        mix(neonColorField)
        mix(neonIntensityField.toBits())
        mix(neonSizeField.toBits())
        mix(neonAnimatedField.hashCode())
        mix(proceduralTextureKindField.hashCode())
        mix(proceduralColorAField)
        mix(proceduralColorBField)
        mix(proceduralScaleField.toBits())
        mix(proceduralDensityField.toBits())
        mix(proceduralIntensityField.toBits())
        mix(flowColorField)
        mix(flowSpeedField.toBits())
        mix(flowWidthField.toBits())
        mix(flowAngleDegField.toBits())
        mix(flowModeField)
        mix(energyColorField)
        mix(energyIntensityField.toBits())
        mix(energyFrequencyField.toBits())
        mix(energySpeedField.toBits())
        mix(energyPatternField)
        mix(filterBrightnessField.toBits())
        mix(filterContrastField.toBits())
        mix(filterGrayscaleField.toBits())
        mix(filterSepiaField.toBits())
        mix(filterHueRotateDegField.toBits())
        mix(filterSaturateField.toBits())
        mix(filterInvertField.toBits())
        mix(filterBlurField.toBits())
        mix(backdropBlurField.toBits())
        mix(blendModeField.ordinal)
        return result
    }

    /** 从另一个 StyleVisual 复制所有字段。 */
    fun copyFrom(other: StyleVisual) {
        cornerRadiusTLField = other.cornerRadiusTLField
        cornerRadiusTRField = other.cornerRadiusTRField
        cornerRadiusBRField = other.cornerRadiusBRField
        cornerRadiusBLField = other.cornerRadiusBLField
        borderWidthField = other.borderWidthField
        borderColorField = other.borderColorField
        gradientTypeField = other.gradientTypeField
        gradientAngleRadiansField = other.gradientAngleRadiansField
        gradientStopsField =
            if (other.gradientStopsField.isEmpty()) emptyList() else ArrayList(other.gradientStopsField)
        radialCenterXField = other.radialCenterXField
        radialCenterYField = other.radialCenterYField
        radialRadiusField = other.radialRadiusField
        shadowOffsetXField = other.shadowOffsetXField
        shadowOffsetYField = other.shadowOffsetYField
        shadowBlurField = other.shadowBlurField
        shadowColorField = other.shadowColorField
        shadowSpreadField = other.shadowSpreadField
        shadowInsetField = other.shadowInsetField
        boxShadowsField = if (other.boxShadowsField.isEmpty()) emptyList() else ArrayList(other.boxShadowsField)
        maskTypeField = other.maskTypeField
        maskParam1Field = other.maskParam1Field
        maskParam2Field = other.maskParam2Field
        maskParam3Field = other.maskParam3Field
        neonColorField = other.neonColorField
        neonIntensityField = other.neonIntensityField
        neonSizeField = other.neonSizeField
        neonAnimatedField = other.neonAnimatedField
        rippleColorField = other.rippleColorField
        rippleDurationField = other.rippleDurationField
        rippleRadiusField = other.rippleRadiusField
        proceduralTextureKindField = other.proceduralTextureKindField
        proceduralColorAField = other.proceduralColorAField
        proceduralColorBField = other.proceduralColorBField
        proceduralScaleField = other.proceduralScaleField
        proceduralDensityField = other.proceduralDensityField
        proceduralIntensityField = other.proceduralIntensityField
        flowColorField = other.flowColorField
        flowSpeedField = other.flowSpeedField
        flowWidthField = other.flowWidthField
        flowAngleDegField = other.flowAngleDegField
        flowModeField = other.flowModeField
        flowOnProgressField = other.flowOnProgressField
        energyColorField = other.energyColorField
        energyIntensityField = other.energyIntensityField
        energyFrequencyField = other.energyFrequencyField
        energySpeedField = other.energySpeedField
        energyPatternField = other.energyPatternField
        filterBrightnessField = other.filterBrightnessField
        filterContrastField = other.filterContrastField
        filterGrayscaleField = other.filterGrayscaleField
        filterSepiaField = other.filterSepiaField
        filterHueRotateDegField = other.filterHueRotateDegField
        filterSaturateField = other.filterSaturateField
        filterInvertField = other.filterInvertField
        filterBlurField = other.filterBlurField
        backdropBlurField = other.backdropBlurField
        blendModeField = other.blendModeField
    }
}
